import { Plugin } from "@ckeditor/ckeditor5-core";
import { ModelRange as Range } from "@ckeditor/ckeditor5-engine";

import ShowCharactersCommand from "./ShowCharactersCommand";

const characters = ["\u202F", "\u2007", "\u2060", "\u00A0"];

export default class ShowCharactersEditing extends Plugin {
  init() {
    const editor = this.editor;
    this._defineConverters();
    this._defineSchema();

    const command = new ShowCharactersCommand(editor, true);
    editor.commands.add("showCharacters", command);

    if (editor.plugins.has("TrackChangesEditing")) {
      const trackChangesEditing = editor.plugins.get("TrackChangesEditing");
      trackChangesEditing.enableCommand("showCharacters");
    }

    editor.on("cde:showCharacters", (_eventInfo, ...args) => {
      const value = !!args[0];
      editor.set("showCharacters", value);
      command.value = value;

      const { mapper, view } = editor.editing;

      // Keep track of the current selection (from the Model)
      const selection = view.document.selection;
      const modelAnchor = mapper.toModelPosition(selection.anchor);
      const modelFocus = mapper.toModelPosition(selection.focus);

      view.change((writer) => {
        // Create a wrapper component for (un)wrapping
        const wrapper = writer.createAttributeElement("span", {
          class: "spaceCharacter",
        });

        // Start at the beginning of the content
        let position = writer.createPositionAt(writer.document.getRoot(), 0);
        while (position) {
          // Get a TreeWalker from the current position
          const walker = position.getWalker({
            ignoreElementEnd: true,
            singleCharacters: value,
          });
          // Clear the position so we can drop out of the while() if we don't find anything
          position = null;

          // Iterate through the TreeWalker
          for (const element of walker) {
            // If we're wrapping elements and we find one of the characters
            if (
              value &&
              element.type === "text" &&
              characters.includes(element.item.data)
            ) {
              // Wrap the element and set the position to immediately follow the wrapper
              const range = writer.wrap(
                writer.createRangeOn(element.item),
                wrapper,
              );
              position = range.end;
              break;
            }
            // If we're unwrapping elements and we find a span.spaceCharacter
            if (
              !value &&
              element.type === "elementStart" &&
              element.item.is("view:element", "span") &&
              element.item.hasClass("spaceCharacter")
            ) {
              // Set the position to the just before the element (in case it's double-wrapped), then unwrap it
              position = writer.createPositionBefore(element.item);
              writer.unwrap(writer.createRangeOn(element.item), wrapper);
              break;
            }
          }
        }

        // Once we're done, set the selection to what it was before
        const viewAnchor = mapper.toViewPosition(modelAnchor);
        const viewFocus = mapper.toViewPosition(modelFocus);
        writer.setSelection(viewAnchor);
        writer.setSelectionFocus(viewFocus);
      });
    });
  }

  _defineConverters() {
    const editor = this.editor;
    const conversion = editor.conversion;

    conversion.for("editingDowncast").add((dispatcher) => {
      dispatcher.on(
        "insert:$text",
        (_event, data, conversionApi) => {
          // Abort if the change cannot be consumed from the queue (because it already has)
          if (!conversionApi.consumable.consume(data.item, "insert")) return;

          const viewWriter = conversionApi.writer;

          let modelPosition = data.range.start;
          let viewPosition = conversionApi.mapper.toViewPosition(modelPosition);

          // If the command is not enabled or active, just add the text
          const command = editor.commands.get("showCharacters");
          if (!command.isEnabled || !command.value) {
            const viewElement = viewWriter.createText(data.item.data);
            viewWriter.insert(viewPosition, viewElement);
            return;
          }

          // If the command is enabled and active, do the complicated stuff
          const chunks = data.item.data.split(/([\u202F\u2007\u2060\u00A0])/);
          chunks.map((chunk) => {
            if (chunk === "") return;

            const viewElement = viewWriter.createText(chunk);
            viewWriter.insert(viewPosition, viewElement);

            if (characters.includes(chunk)) {
              const wrapper = viewWriter.createAttributeElement("span", {
                class: "spaceCharacter",
              });

              const modelRange = new Range(
                modelPosition,
                modelPosition.getShiftedBy(chunk.length),
              );
              const viewRange = conversionApi.mapper.toViewRange(modelRange);

              viewWriter.wrap(viewRange, wrapper);
            }

            modelPosition = modelPosition.getShiftedBy(chunk.length);
            viewPosition = conversionApi.mapper.toViewPosition(modelPosition);
          });
        },
        { priority: "high" },
      );
    });

    conversion.for("editingDowncast").elementToElement({
      model: "spaceCharacter",
      view: {
        classes: "spaceCharacter",
        name: "span",
      },
    });

    conversion.for("upcast").elementToElement({
      model: "spaceCharacter",
      view: {
        classes: "spaceCharacter",
        name: "span",
      },
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("spaceCharacter", {
      allowContentOf: "$text",
      allowWhere: "$text",
      isObject: true,
    });
  }
}
